import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OperationInvoicesPanel } from '@/modules/billing/components';
import {
  CommercialStatusBadge,
  CommercialTypeBadge,
  OperationLinksPanel,
} from '@/modules/commercial/components';
import { useClients } from '@/modules/commercial/hooks';
import {
  createReservationForOperation,
  getCommercialOperation,
} from '@/modules/commercial/services';
import {
  BILLABLE_OPERATION_STATUSES,
  COMMERCIAL_OPERATION_STATUS_LABELS,
} from '@/modules/commercial/types';
import { listItems } from '@/modules/inventory/services';
import type { InventoryItem } from '@/modules/inventory/types';
import { modulePaths } from '@/routes/paths';
import { EntityAuditTrail } from '@/shared/components/EntityAuditTrail';
import { useAuth } from '@/shared/hooks/useAuth';

export function OperationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { profile, hasPermission } = useAuth();
  const canWrite = hasPermission('commercial.write');
  const { clients } = useClients();

  const [operation, setOperation] = useState<Awaited<
    ReturnType<typeof getCommercialOperation>
  >>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void Promise.all([getCommercialOperation(id), listItems()])
      .then(([op, allItems]) => {
        if (!op) {
          setError('Operación no encontrada.');
          return;
        }
        setOperation(op);
        setItems(allItems);
      })
      .catch(() => setError('Error al cargar la operación.'))
      .finally(() => setLoading(false));
  }, [id]);

  async function handleGenerateReservation() {
    if (!id || !profile || !operation) return;
    const ok = window.confirm(
      '¿Generar una reserva en borrador vinculada a esta operación?',
    );
    if (!ok) return;
    setGenerating(true);
    setError(null);
    try {
      const updated = await createReservationForOperation(id, profile.uid);
      setOperation(updated);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'No se pudo generar la reserva.',
      );
    } finally {
      setGenerating(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;
  if (!operation) {
    return (
      <section>
        <p className="auth-error">{error ?? 'Operación no encontrada.'}</p>
        <Link to={`${modulePaths.commercial}/operaciones`}>← Volver</Link>
      </section>
    );
  }

  const clientName = operation.clientId
    ? (clients.find((c) => c.id === operation.clientId)?.name ?? '—')
    : '—';

  const itemLabel = (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    return item ? `${item.sku} — ${item.name}` : itemId;
  };

  const isBillable = BILLABLE_OPERATION_STATUSES.includes(operation.status);

  return (
    <section className="reservation-detail">
      <p>
        <Link to={`${modulePaths.commercial}/operaciones`}>
          ← Operaciones
        </Link>
      </p>

      <div className="inventory-page-header">
        <h1>{operation.title}</h1>
        <CommercialTypeBadge type={operation.type} />
        <CommercialStatusBadge status={operation.status} />
      </div>

      {canWrite ? (
        <div className="inventory-actions">
          <Link
            to={`${modulePaths.commercial}/operaciones/${operation.id}/editar`}
            className="auth-button inventory-page-header__btn"
          >
            Editar
          </Link>
          {operation.type === 'rental' && !operation.reservationId ? (
            <button
              type="button"
              className="auth-button auth-button--secondary"
              disabled={generating}
              onClick={() => void handleGenerateReservation()}
            >
              {generating ? 'Generando…' : 'Generar reserva'}
            </button>
          ) : null}
        </div>
      ) : null}

      <dl className="reservation-detail-grid">
        <dt>Cliente</dt>
        <dd>{clientName}</dd>
        <dt>Estado</dt>
        <dd>{COMMERCIAL_OPERATION_STATUS_LABELS[operation.status]}</dd>
        {operation.type === 'rental' ? (
          <>
            <dt>Desde</dt>
            <dd>{operation.startDate || '—'}</dd>
            <dt>Hasta</dt>
            <dd>{operation.endDate || '—'}</dd>
          </>
        ) : null}
        <dt>Descripción</dt>
        <dd>{operation.description || '—'}</dd>
        <dt>Subtotal</dt>
        <dd>
          {operation.subtotal > 0
            ? `$ ${operation.subtotal.toLocaleString('es-AR')}`
            : '—'}
        </dd>
        <dt>Impuestos</dt>
        <dd>
          {operation.taxAmount > 0
            ? `$ ${operation.taxAmount.toLocaleString('es-AR')}`
            : '—'}
        </dd>
        <dt>Total</dt>
        <dd>
          {operation.total > 0
            ? `$ ${operation.total.toLocaleString('es-AR')}`
            : '—'}
        </dd>
        <dt>Facturable (futuro)</dt>
        <dd>{isBillable ? 'Sí — listo para ARCA' : 'No'}</dd>
        <dt>Notas</dt>
        <dd>{operation.notes || '—'}</dd>
        <dt>Actualizada</dt>
        <dd>{operation.updatedAt.toLocaleString('es-AR')}</dd>
      </dl>

      <h2>Artículos</h2>
      {operation.inventoryItemIds.length === 0 ? (
        <p className="auth-muted">Sin artículos.</p>
      ) : (
        <ul>
          {operation.inventoryItemIds.map((itemId) => (
            <li key={itemId}>
              <Link to={`${modulePaths.inventory}/${itemId}`}>
                {itemLabel(itemId)}
              </Link>
            </li>
          ))}
        </ul>
      )}

      <OperationLinksPanel operation={operation} />

      <OperationInvoicesPanel
        commercialOperationId={operation.id}
        billable={isBillable}
      />

      <EntityAuditTrail
        entityType="commercialOperation"
        entityId={operation.id}
      />

      {error ? <p className="auth-error">{error}</p> : null}
    </section>
  );
}
